/* Front pocket personalization — Tier 1 */
(function(){
  "use strict";

  var MAX_FILE_BYTES = 10 * 1024 * 1024;
  var OK_EXT  = /\.(png|svg|ai|pdf)$/i;
  var PDF_EXT = /\.(ai|pdf)$/i;

  var OPENTYPE_SRC = "https://cdnjs.cloudflare.com/ajax/libs/opentype.js/1.3.4/opentype.min.js";
  var otPromise = null;
  // Loaded only when a text order is actually placed, like pdf.js above.
  function ensureOpentype(){
    if (otPromise) return otPromise;
    otPromise = new Promise(function(res, rej){
      if (window.opentype) return res(window.opentype);
      var s = document.createElement("script");
      s.src = OPENTYPE_SRC;
      s.onload = function(){ window.opentype ? res(window.opentype) : rej(new Error("opentype missing")); };
      s.onerror = function(){ rej(new Error("opentype blocked")); };
      document.head.appendChild(s);
    });
    return otPromise;
  }
  // Fonts are stored base64 in .txt files: Shopify re-compresses an uploaded
  // .ttf to WOFF2, which opentype.js cannot parse.
  function loadFontFile(url){
    return ensureOpentype().then(function(ot){
      return fetch(url).then(function(r){ if(!r.ok) throw new Error("font fetch"); return r.text(); })
        .then(function(b64){
          var bin = atob(b64.trim()), n = bin.length, u8 = new Uint8Array(n);
          for (var i=0;i<n;i++) u8[i] = bin.charCodeAt(i);
          return ot.parse(u8.buffer);
        });
    });
  }

  var PDFJS_SRC    = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js";
  var PDFJS_WORKER = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";
  var pdfjsPromise = null;

  // Loaded only when someone actually picks a PDF or AI. The PDP already has a
  // conversion problem; this never touches a page load.
  function ensurePdfJs(){
    if (pdfjsPromise) return pdfjsPromise;
    pdfjsPromise = new Promise(function(res, rej){
      if (window.pdfjsLib) return res(window.pdfjsLib);
      var s = document.createElement("script");
      s.src = PDFJS_SRC;
      s.onload = function(){
        if (!window.pdfjsLib) return rej(new Error("pdfjs missing"));
        window.pdfjsLib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER;
        res(window.pdfjsLib);
      };
      s.onerror = function(){ rej(new Error("pdfjs blocked")); };
      document.head.appendChild(s);
    });
    return pdfjsPromise;
  }

  // .ai is a PDF container whenever Illustrator saved it with PDF compatibility
  // on, which is the default. Feed the bytes straight to pdf.js; if it was saved
  // without, this rejects and the caller tells the customer how to fix it.
  function rasterizePdf(file){
    return ensurePdfJs().then(function(pdfjsLib){
      return file.arrayBuffer().then(function(buf){
        return pdfjsLib.getDocument({ data: new Uint8Array(buf) }).promise;
      });
    }).then(function(doc){ return doc.getPage(1); })
      .then(function(page){
        var v1 = page.getViewport({ scale: 1 });
        var scale = Math.min(1600/v1.width, 1600/v1.height, 4);
        var vp = page.getViewport({ scale: scale });
        var c = document.createElement("canvas");
        c.width = Math.ceil(vp.width); c.height = Math.ceil(vp.height);
        return page.render({ canvasContext: c.getContext("2d"), viewport: vp })
                   .promise.then(function(){ return c.toDataURL("image/png"); });
      });
  }

  // Only characters an embroidery machine can actually stitch. Emoji, control
  // characters and zero-width joiners reach the digitizer as garbage.
  function clean(s){
    try { return s.replace(/[^\p{L}\p{N} &'\-.,\/#+()]/gu, ""); }
    catch(e){ return s.replace(/[^A-Za-z0-9 &'\-.,\/#+()]/g, ""); }
  }
  // Removing a character leaves a gap either side of it, and the block is
  // centered, so a stray leading space shifts the whole line. Both stitch.
  function tidy(s){ return s.replace(/\s{2,}/g, " ").trim(); }

  function init(root){
    if (root.__fpm) return; root.__fpm = true;
    var d = root.dataset;

    var THREADS = (d.threads||"").split("\n").map(function(l){
      var p = l.split("|");
      if (p.length < 3) return null;
      return { num:p[0].trim(), name:p[1].trim(), hex:p[2].trim() };
    }).filter(Boolean);
    if (!THREADS.length) THREADS = [{num:"0015",name:"White",hex:"#ffffff"}];

    var F = "https://cdn.shopify.com/s/files/1/0670/3244/9212/files/";
    var FONTS = {
      brandbold:{css:(d.brandHeading||"sans-serif"), w:(d.brandHeadingWeight||700), g:null, minIn:0.40,
                 file:F+"tcf-barlow-700.txt?v=1790101897", expect:"Barlow"},
      brand:    {css:(d.brandBody||"sans-serif"),    w:(d.brandBodyWeight||400),   g:null, minIn:0.50,
                 file:F+"tcf-barlow-400.txt?v=1790101897", expect:"Barlow"},
      bebas:    {css:'"Bebas Neue",Impact,sans-serif',   w:400, g:"Bebas+Neue",              minIn:0.35,
                 file:F+"tcf-bebasneue-400.txt?v=1790101897"},
      block:    {css:'"Archivo Black",Impact,sans-serif',w:400, g:"Archivo+Black",           minIn:0.40,
                 file:F+"tcf-archivoblack-400.txt?v=1790101897"},
      varsity:  {css:'Graduate,"Archivo Black",serif',   w:400, g:"Graduate",                minIn:0.50,
                 file:F+"tcf-graduate-400.txt?v=1790101897"},
      serif:    {css:'"Playfair Display",Georgia,serif', w:700, g:"Playfair+Display:wght@700",minIn:0.60,
                 file:F+"tcf-playfairdisplay-700.txt?v=1790101897"},
      script:   {css:'"Great Vibes",cursive',            w:400, g:"Great+Vibes",             minIn:0.75,
                 file:F+"tcf-greatvibes-400.txt?v=1790101897"}
    };

    var q = function(s){ return root.querySelector(s); };
    var on=q("[data-fpm-on]"), panel=q("[data-fpm-panel]"), stage=q("[data-fpm-stage]"),
        bag=q("[data-fpm-bag]"), pocket=q("[data-fpm-pocket]"), guide=q("[data-fpm-guide]"),
        lines=q("[data-fpm-lines]"), art=q("[data-fpm-art]"), note=q("[data-fpm-note]"),
        input=q("[data-fpm-input]"), fontSel=q("[data-fpm-font]"), threadWrap=q("[data-fpm-threads]"),
        sizeIn=q("[data-fpm-size]"), sizeVal=q("[data-fpm-sizeval]"), sizeLabel=q("[data-fpm-sizetext]"),
        tName=q("[data-fpm-threadname]"), lineCount=q("[data-fpm-linecount]"),
        justify=q("[data-fpm-justify]"), justifyWrap=q("[data-fpm-justifywrap]"),
        monoFields=q("[data-fpm-monofields]"), logoFields=q("[data-fpm-logofields]"),
        drop=q("[data-fpm-drop]"), fileIn=q("[data-fpm-file]"),
        dropMain=q("[data-fpm-dropmain]"), dropSub=q("[data-fpm-dropsub]"),
        bill=q("[data-fpm-bill]"), total=q("[data-fpm-total]"), err=q("[data-fpm-error]"),
        clearBtn=q("[data-fpm-clear]");
    var DROP_HINT = dropSub.textContent;

    function box(s){ var b=(s||"").split(",").map(parseFloat);
      return (b.length===4 && b.every(function(n){return n>=0 && n<=1;})) ? b : null; }
    var P_BLACK = box(d.pocket) || [0,0,1,1];
    var P_WHITE = box(d.pocketWhite) || P_BLACK;
    // Selected variant id -> "white" | "black", from the product's own variant titles.
    var VARIANT_COLOR = {};
    (d.variants||"").split("|").forEach(function(p){
      var i=p.indexOf(":"); if(i>0) VARIANT_COLOR[p.slice(0,i)] = /white/i.test(p.slice(i+1)) ? "white" : "black";
    });

    // PANEL is the physical front pocket. It sets the pixel-to-inch scale and
    // nothing else. FIELD is the stitchable area inside it, inset on every edge
    // for hoop clearance. Never conflate the two.
    var PANEL_W = parseFloat(d.pocketW)||8.5, PANEL_H = parseFloat(d.pocketH)||11;
    var INSET = Math.min(Math.max(parseFloat(d.hoopInset)||0, 0), 60) / 100;
    var FIELD_W = PANEL_W*(1-INSET), FIELD_H = PANEL_H*(1-INSET);

    var MAX_LINES = parseInt(d.maxLines,10)||3, MAX_CHARS = parseInt(d.maxChars,10)||20;
    var BASE = parseFloat(d.basePrice)||0;
    var PR = { mono:parseFloat(d.monoPrice)||0, logo:parseFloat(d.logoPrice)||0 };
    var S = { on:false, mode:"mono", lines:[], font:(fontSel.value||"brandbold"), thread:0, size:2,
              justify:!!d.justifyDefault, file:null, fileName:"", fitW:0, fitH:0,
              scaled:false, small:false, stripped:false, capped:false, lineIn:[], maxSize:0 };
    justify.checked = S.justify;
    var loaded = {};

    // The slider must never run past what the stitch field can hold, or it looks
    // broken: the number keeps climbing while the preview stops changing.
    function syncSlider(){
      var cap = (S.mode==="mono" && S.maxSize) ? S.maxSize : (S.mode==="mono" ? Math.min(6,FIELD_H) : FIELD_W);
      sizeIn.max = String(cap);
      if(S.size > cap) S.size = cap;
      if(parseFloat(sizeIn.value)!==S.size) sizeIn.value=String(S.size);
      sizeVal.textContent=S.size.toFixed(2)+" in";
    }
    // Snapl can't look up "Tour Caddy Bold" — the ticket has to name the real
    // typeface and weight so they can match it to a machine font.
    function fontSpec(k){
      var f=FONTS[k]||FONTS.brandbold;
      var m=/^\s*"?([^",]+)"?/.exec(f.css||"");
      var fam=(m?m[1]:"").trim();
      return fam ? (fam+" "+f.w) : String(f.w);
    }
    // The lettering choice has to survive on the order as plain data. Until now
    // it lived only inside the generated SVG, so an order that lost its SVG could
    // not be rebuilt without asking the customer which font they had picked.
    // Read by value rather than selectedIndex: the two can drift apart.
    function fontLabel(){
      for(var i=0;i<fontSel.options.length;i++){
        if(fontSel.options[i].value===S.font) return fontSel.options[i].text;
      }
      return S.font;
    }
    function fieldText(){ return FIELD_W.toFixed(1)+" x "+FIELD_H.toFixed(1)+" in"; }
    function showError(m){ err.textContent=m; err.hidden=!m; }

    function loadFont(k){
      var f=FONTS[k];
      if (!f || !f.g || loaded[k]) return;
      loaded[k]=true;
      var l=document.createElement("link"); l.rel="stylesheet";
      l.href="https://fonts.googleapis.com/css2?family="+f.g+"&display=swap";
      document.head.appendChild(l);
      if(document.fonts && document.fonts.ready) document.fonts.ready.then(function(){ place(); });
    }
    function money(n){ return "$"+n.toFixed(2); }

    // The theme's variant picker doesn't use options[Color] names, so read the
    // variant id the product form will actually submit and map it to a colour.
    function currentColor(){
      var f=productForm(), el=f && f.querySelector('[name="id"]');
      return (el && VARIANT_COLOR[String(el.value)]) || "black";
    }
    // Which photo is on screen, and which pocket box belongs to it.
    function view(){
      var c=currentColor(), white = c==="white" && !!d.previewWhite;
      return { src: white ? d.previewWhite : d.previewBlack,
               P: white ? P_WHITE : P_BLACK,
               stand: c==="white" && !d.previewWhite };
    }
    function setBag(){
      var v=view();
      if(v.src && bag.getAttribute("src")!==v.src) bag.setAttribute("src",v.src);
      return v.stand;
    }

    function parseLines(){
      var stripped=false;
      var out = input.value.split("\n").map(function(raw){
        var c = clean(raw);
        if (c !== raw) stripped = true;
        return tidy(c).slice(0,MAX_CHARS);
      });
      S.stripped = stripped;
      return out.filter(function(s){ return s.length; }).slice(0,MAX_LINES);
    }

    function buildLines(){
      lines.textContent="";
      S.lines.forEach(function(t){
        var s=document.createElement("span");
        s.className="fpm__line"; s.textContent=t;
        lines.appendChild(s);
      });
    }
    // How far the short lines may grow before the stack runs out of height.
    // "allowed" is the height budget expressed in multiples of the base line.
    // Returns the largest cap C with sum(min(ratio_i, C)) <= allowed.
    function capFor(rs, allowed){
      var a=rs.slice().sort(function(x,y){ return x-y; }), sum=0;
      for(var i=0;i<a.length;i++){
        var C=(allowed-sum)/(a.length-i);
        if(C<=a[i]) return C;
        sum+=a[i];
      }
      return Infinity;
    }
    function lineEls(){ return Array.prototype.slice.call(lines.children); }
    function clearScale(){ lineEls().forEach(function(el){ el.style.transform="none"; el.style.fontSize=""; }); }
    var SXnow = 1;
    function naturalWidths(){ return lineEls().map(function(el){ return el.getBoundingClientRect().width / SXnow; }); }

    function place(){
      var w=stage.clientWidth, h=stage.clientHeight;
      if(!w||!h) return;

      var P=view().P;
      var L=P[0]*w, T=P[1]*h, R=P[2]*w, B=P[3]*h;
      var panelW=R-L, panelH=B-T;
      // Scale comes from the pocket's height. If the photo shows the pocket
      // turned slightly away (white bag), its width is foreshortened; SX squashes
      // the design horizontally by the same amount so it sits on the pocket the
      // way real stitching would. SX is ~1 on a square-on photo.
      var ppi = panelH/PANEL_H;
      var SX = Math.min(Math.max((panelW/PANEL_W)/ppi, 0.5), 1.5);
      var fieldWpx = FIELD_W*ppi, fieldHpx = FIELD_H*ppi;       // unsquashed layout box
      var cx = L+panelW/2, cy = T+panelH/2;

      pocket.style.left=(cx-fieldWpx/2)+"px"; pocket.style.top=(cy-fieldHpx/2)+"px";
      pocket.style.width=fieldWpx+"px"; pocket.style.height=fieldHpx+"px";
      pocket.style.transformOrigin="center center";
      pocket.style.transform = Math.abs(SX-1)>0.005 ? "scaleX("+SX.toFixed(4)+")" : "none";
      SXnow = Math.abs(SX-1)>0.005 ? SX : 1;
      guide.style.left=(cx-fieldWpx*SX/2)+"px"; guide.style.top=(cy-fieldHpx/2)+"px";
      guide.style.width=(fieldWpx*SX)+"px"; guide.style.height=fieldHpx+"px";

      var stand=setBag(), msg="";
      S.scaled=false; S.fitW=0; S.fitH=0; S.small=false; S.capped=false;

      if(S.mode==="mono"){
        lines.hidden=false; art.hidden=true;
        var f=FONTS[S.font]||FONTS.brandbold;
        lines.style.fontFamily=f.css; lines.style.fontWeight=f.w;
        lines.style.color=THREADS[S.thread].hex;
        lines.style.textShadow="0 1px 0 rgba(0,0,0,.45)";

        if(S.lines.length && ppi){
          var LH=1.16;
          var basePx=S.size*ppi;
          lines.style.fontSize=basePx+"px";
          clearScale();
          // Match line widths by making the shorter lines' letters bigger, never
          // by stretching them. The slider always sets the LONGEST line and the
          // short lines grow as far as the stitch field allows, so the slider
          // keeps working with matching on.
          var nat=naturalWidths();
          var natMax=Math.max.apply(null,nat);
          var match = S.justify && nat.length>1 && natMax>0;
          var ratio = nat.map(function(n){ return (match && n>0) ? natMax/n : 1; });

          // Cap the slider at the biggest letter height that still fits: width is
          // set by the longest line (every matched line ends up that wide) and
          // height by the lines stacked at the base size.
          var wPerIn = natMax/(S.size||1);
          var maxIn = Math.min(fieldWpx/(wPerIn||1), fieldHpx/((S.lines.length*LH*ppi)||1), 6, FIELD_H);
          maxIn = Math.max(0.5, Math.floor(maxIn/0.25)*0.25);
          S.maxSize = maxIn;
          if(S.size > maxIn+0.001){
            S.size = maxIn; basePx = S.size*ppi;
            lines.style.fontSize = basePx+"px";
            nat = naturalWidths(); natMax = Math.max.apply(null,nat);   // ratios are scale-free
          }
          syncSlider();

          // Safety net for rounding and odd font metrics.
          var kW=Math.min(fieldWpx/(natMax||1), 1);
          if(kW<0.999){ S.scaled=true; basePx=S.size*kW*ppi; lines.style.fontSize=basePx+"px"; }
          var base=basePx/ppi;

          var C=capFor(ratio, fieldHpx/((basePx*LH)||1));
          if(C<1){ base*=C; basePx=base*ppi; lines.style.fontSize=basePx+"px"; S.scaled=true; C=1; }
          var used=ratio.map(function(r){ return Math.min(r,C); });
          lineEls().forEach(function(el,i){ el.style.fontSize = used[i]===1 ? "" : used[i].toFixed(4)+"em"; });

          var wNow=Math.max.apply(null,naturalWidths());
          var blockH=lines.getBoundingClientRect().height;
          if(blockH>fieldHpx+0.5){            // real font metrics differ from the 1.16 estimate
            var k2=fieldHpx/blockH;
            base*=k2; basePx=base*ppi; S.scaled=true;
            lines.style.fontSize=basePx+"px";
            wNow=Math.max.apply(null,naturalWidths());
            blockH=lines.getBoundingClientRect().height;
          }

          S.fitW=wNow/ppi; S.fitH=blockH/ppi;
          S.lineIn = used.map(function(u){ return base*u; });
          S.capped = match && Math.max.apply(null,ratio) > C+0.001;
          S.small = Math.min.apply(null,S.lineIn) < f.minIn;

          msg=S.fitW.toFixed(1)+" x "+S.fitH.toFixed(1)+" in";
          if(S.scaled) msg+=" — scaled to fit the "+fieldText()+" stitch area";
          if(S.capped) msg+=" — shorter lines widened as far as the stitch area allows";
          if(S.small) msg+=" — below the "+f.minIn.toFixed(2)+" in this lettering needs to stitch cleanly";
        } else {
          S.maxSize=0; syncSlider();
          lines.style.fontSize=(S.size*ppi)+"px";
          msg="Stitch area "+fieldText();
        }
      } else {
        lines.hidden=true; art.hidden=!(S.file && art.getAttribute("src"));
        if(S.file && art.getAttribute("src")){
          var ar = art.naturalWidth ? art.naturalHeight/art.naturalWidth : 1;
          var wIn=Math.min(S.size, FIELD_W);
          if(wIn*ar > FIELD_H){ wIn=FIELD_H/ar; }
          if(wIn < S.size - 0.001) S.scaled=true;
          art.style.width=(wIn*ppi)+"px"; art.style.height="auto";
          S.fitW=wIn; S.fitH=wIn*ar;
          msg=S.fitW.toFixed(1)+" x "+S.fitH.toFixed(1)+" in";
          if(S.scaled) msg+=" — scaled to fit the "+fieldText()+" stitch area";
        } else {
          msg="Stitch area "+fieldText();
        }
      }
      if(S.mode==="mono" && S.stripped){
        msg=(msg?msg+" — ":"")+"some characters can't be stitched and were removed";
      }
      if(stand) msg=(msg?msg+" — ":"")+"shown on the black bag; yours will be white";
      note.textContent=msg;
      if(S.small||S.stripped) note.setAttribute("data-warn",""); else note.removeAttribute("data-warn");
      syncProps();
    }

    function productForm(){ return document.querySelector('form[action*="/cart/add"]'); }
    function setProp(form,k,v){
      var el=form.querySelector('input[data-fpm-prop][data-fpm-key="'+k+'"]');
      if(!el){ el=document.createElement("input"); el.type="hidden";
        el.setAttribute("data-fpm-prop",""); el.setAttribute("data-fpm-key",k); form.appendChild(el); }
      el.name="properties["+k+"]"; el.value=v;
    }
    function ready(){
      return S.on && (S.mode==="mono" ? S.lines.length>0
                                      : !!(S.file && art.getAttribute("src")));
    }
    function sizeText(){
      if(S.fitW && S.fitH) return S.fitW.toFixed(2)+" x "+S.fitH.toFixed(2)+" in";
      return S.mode==="mono" ? (S.size.toFixed(2)+" in letter height")
                             : (S.size.toFixed(2)+" in wide");
    }
    function syncProps(){
      var form=productForm(); if(!form) return;
      Array.prototype.forEach.call(form.querySelectorAll("[data-fpm-prop]"),function(n){n.remove();});
      if(!ready()) return;
      setProp(form,"Placement","Front pocket, centered");
      setProp(form,"Stitch Field",fieldText());
      if(S.mode==="mono"){
        // The SVG is what gets stitched, so the order carries the file plus only
        // what a human needs to sanity-check it: the words, the thread, the size
        // and where it goes. Font, line count and per-line heights all live in
        // the vector itself — repeating them invites the two to disagree.
        setProp(form,"Embroidery","Front pocket text");
        setProp(form,"Text",S.lines.join(" / "));
        setProp(form,"Lettering",fontLabel()+" ("+fontSpec(S.font)+")");
        setProp(form,"Thread",THREADS[S.thread].num+" "+THREADS[S.thread].name);
        setProp(form,"Design Size",sizeText());
        setProp(form,"Artwork Source","Vector generated from this text — outlines at final size, ready to digitize");
        if(S.small) setProp(form,"Check Before Stitching","Letter height below recommended minimum");
      } else {
        setProp(form,"Embroidery","Front pocket logo");
        setProp(form,"Design Size",sizeText());
        // "Artwork File", "Artwork Source" and "Digitization" are deliberately
        // NOT written here. All three assert that a customer file is attached,
        // but the file can only ever be attached by addBag()'s multipart
        // request - it is a File object and cannot live in a hidden input.
        // Order #2366 (2 Oct 2026) was paid for carrying all three of these and
        // no artwork, because a submit that bypassed this handler took the
        // hidden inputs and left the file in memory. They are written in
        // addBag() now, beside the file itself, so the claim and the file
        // cannot be separated.
      }
    }

    // Renders exactly what the customer approved onto the bag photo and returns
    // a PNG. This IS the proof — no proof is emailed afterwards, so the record
    // has to be made at the moment they add to cart.
    function loadProofBag(){
      return new Promise(function(res, rej){
        var src = bag.getAttribute("src");
        if(!src) return rej(new Error("no bag image"));
        var im = new Image();
        im.crossOrigin = "anonymous";
        im.onload = function(){ res(im); };
        im.onerror = function(){ rej(new Error("bag not cross-origin readable")); };
        im.src = src;
      });
    }

    function buildProof(){
      return loadProofBag().then(function(im){
        var W = 900, H = Math.round(W * im.naturalHeight / im.naturalWidth);
        var c = document.createElement("canvas");
        c.width = W; c.height = H;
        var g = c.getContext("2d");
        g.drawImage(im, 0, 0, W, H);

        var P=view().P;
        var L=P[0]*W, T=P[1]*H, R=P[2]*W, B=P[3]*H;
        var panelW=R-L, panelH=B-T;
        var ppi = panelH/PANEL_H;
        var SX = Math.min(Math.max((panelW/PANEL_W)/ppi, 0.5), 1.5);
        var fW = FIELD_W*ppi, fH = FIELD_H*ppi;
        var cx = L+panelW/2, cy = T+panelH/2;
        g.save();
        g.translate(cx, cy); g.scale(SX, 1); g.translate(-cx, -cy);

        if(S.mode === "mono"){
          var f = FONTS[S.font]||FONTS.brandbold;
          var px = S.size*ppi;
          g.font = f.w+" "+px+"px "+f.css;
          var widths = S.lines.map(function(t){ return g.measureText(t).width; });
          var wMax = Math.max.apply(null, widths);
          var match = S.justify && S.lines.length>1 && wMax>0;
          var ratio = widths.map(function(w){ return (match && w>0) ? wMax/w : 1; });
          var lh = 1.16;
          var kW = Math.min(fW/(wMax||1), 1);
          px *= kW;
          var C = capFor(ratio, fH/((px*lh)||1));
          if(C < 1){ px *= C; C = 1; }
          var used = ratio.map(function(r){ return Math.min(r,C); });
          var blockH = used.reduce(function(sum,u){ return sum + px*u*lh; }, 0);
          g.fillStyle = THREADS[S.thread].hex;
          g.textAlign = "left"; g.textBaseline = "middle";
          var y = cy - blockH/2;
          S.lines.forEach(function(t,i){
            var size = px*used[i];
            g.font = f.w+" "+size+"px "+f.css;
            var lw = g.measureText(t).width;
            y += size*lh/2;
            g.fillText(t, cx - lw/2, y);
            y += size*lh/2;
          });
        } else if (art.getAttribute("src")) {
          var ar = art.naturalWidth ? art.naturalHeight/art.naturalWidth : 1;
          var wIn = Math.min(S.size, FIELD_W);
          if(wIn*ar > FIELD_H) wIn = FIELD_H/ar;
          var dw = wIn*ppi, dh = dw*ar;
          g.drawImage(art, cx-dw/2, cy-dh/2, dw, dh);
        }
        g.restore();

        return new Promise(function(res){ c.toBlob(res, "image/png"); });
      });
    }

    // A vector of the customer's text, in inches, with the letters as outlines.
    // This is what Snapl digitizes from, so text and logo orders arrive as the
    // same kind of file. Never blocks the sale: a failure just means no SVG.
    function buildStitchSvg(){
      var f = FONTS[S.font] || FONTS.brandbold;
      if (!f.file) return Promise.resolve(null);
      // The brand options follow the theme's font setting. If someone changes it,
      // the file on the CDN is no longer the font on screen, so send nothing.
      if (f.expect && (f.css||"").toLowerCase().indexOf(f.expect.toLowerCase()) !== 0) return Promise.resolve(null);

      return loadFontFile(f.file).then(function(font){
        var LHs = 1.16, lines = S.lines.slice();
        var adv = function(t, size){ return font.getAdvanceWidth(t, size, {kerning:true}); };
        var size = S.size;
        var w0 = lines.map(function(t){ return adv(t, size); });
        var wMax = Math.max.apply(null, w0);
        var match = S.justify && lines.length>1 && wMax>0;
        var ratio = w0.map(function(w){ return (match && w>0) ? wMax/w : 1; });

        var kW = Math.min(FIELD_W/(wMax||1), 1);      // same fitting rules as the preview
        if(kW < 1) size *= kW;
        var C = capFor(ratio, FIELD_H/((size*LHs)||1));
        if(C < 1){ size *= C; C = 1; }
        var used = ratio.map(function(r){ return Math.min(r,C); });

        var sizes = used.map(function(u){ return size*u; });
        var widths = lines.map(function(t,i){ return adv(t, sizes[i]); });
        var W = Math.max.apply(null, widths);
        var H = sizes.reduce(function(sum,z){ return sum + z*LHs; }, 0);

        // Centre each line's cap block on its row, matching the on-screen preview.
        var upm = font.unitsPerEm || 1000;
        var cap = (font.tables && font.tables.os2 && font.tables.os2.sCapHeight)
                  ? font.tables.os2.sCapHeight/upm : 0.72;
        var y = 0, paths = [];
        lines.forEach(function(t,i){
          var z = sizes[i];
          y += z*LHs/2;
          var pth = font.getPath(t, (W-widths[i])/2, y + (cap*z)/2, z, {kerning:true});
          paths.push('  <path d="' + pth.toPathData(4) + '"/>');
          y += z*LHs/2;
        });

        function esc(t){ return String(t).replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;").replace(/--/g,"- -"); }
        var head = [
          "Tour Caddy front pocket embroidery",
          "Text: " + lines.join(" / "),
          "Lettering: " + fontLabel() + " (" + fontSpec(S.font) + ")",
          "Thread: " + THREADS[S.thread].num + " " + THREADS[S.thread].name,
          "Letter height per line: " + sizes.map(function(z){ return z.toFixed(2)+" in"; }).join(" / "),
          "Design size: " + W.toFixed(2) + " x " + H.toFixed(2) + " in",
          "Stitch field: " + fieldText() + ", centered on the front pocket",
          "Units: 1 user unit = 1 inch. Outlines, not live text."
        ].map(esc).join("\n     ");

        var svg = '<?xml version="1.0" encoding="UTF-8"?>\n' +
          "<!-- " + head + " -->\n" +
          '<svg xmlns="http://www.w3.org/2000/svg" width="' + W.toFixed(3) + 'in" height="' + H.toFixed(3) + 'in"' +
          ' viewBox="0 0 ' + W.toFixed(4) + " " + H.toFixed(4) + '">\n' +
          "  <title>" + esc(lines.join(" / ")) + "</title>\n" +
          paths.join("\n") + "\n</svg>\n";

        S.stitchSize = W.toFixed(2) + " x " + H.toFixed(2) + " in";
        return new Blob([svg], { type: "image/svg+xml" });
      }).catch(function(){ return null; });
    }

    function renderBill(){
      if(S.mode==="mono"){
        bill.textContent="Bag "+money(BASE)+" + text "+money(PR.mono);
        total.textContent=money(BASE+PR.mono);
      } else {
        bill.textContent="Bag "+money(BASE)+" + logo "+money(PR.logo)+" (digitizing included)";
        total.textContent=money(BASE+PR.logo);
      }
    }

    function render(){
      panel.hidden=!S.on;
      if(S.on) loadFont(S.font);
      monoFields.hidden=S.mode!=="mono";
      logoFields.hidden=S.mode!=="logo";
      if(clearBtn) clearBtn.hidden=!S.file;
      justifyWrap.hidden = S.lines.length<2;
      if(S.mode==="mono"){
        sizeLabel.textContent=(S.justify && S.lines.length>1) ? "Letter height (longest line)" : "Letter height";
        sizeIn.min="0.5";
      } else {
        sizeLabel.textContent="Logo width";
        sizeIn.min="1";
      }
      sizeIn.step="0.25";
      syncSlider();
      tName.textContent=THREADS[S.thread].num+" "+THREADS[S.thread].name;
      lineCount.textContent=S.lines.length+" of "+MAX_LINES+" lines";
      buildLines(); renderBill(); place();
    }

    THREADS.forEach(function(t,i){
      var b=document.createElement("button");
      b.type="button"; b.className="fpm__sw"; b.style.background=t.hex;
      b.title=t.num+" "+t.name; b.setAttribute("aria-label","Thread "+t.num+" "+t.name);
      b.setAttribute("aria-pressed", i===0?"true":"false");
      b.addEventListener("click",function(){
        S.thread=i;
        Array.prototype.forEach.call(threadWrap.children,function(c,j){
          c.setAttribute("aria-pressed", j===i?"true":"false"); });
        render();
      });
      threadWrap.appendChild(b);
    });

    Array.prototype.forEach.call(root.querySelectorAll("[data-fpm-mode]"),function(btn){
      btn.addEventListener("click",function(){
        S.mode=btn.getAttribute("data-fpm-mode");
        S.size = S.mode==="mono" ? 2 : Math.min(4, FIELD_W);
        showError("");
        Array.prototype.forEach.call(root.querySelectorAll("[data-fpm-mode]"),function(o){
          o.setAttribute("aria-pressed", o===btn?"true":"false"); });
        render();
      });
    });

    on.addEventListener("change",function(e){ S.on=e.target.checked; showError(""); render(); });
    input.addEventListener("input",function(){ S.lines=parseLines(); render(); });
    fontSel.addEventListener("change",function(e){ S.font=e.target.value; loadFont(S.font); render(); });
    sizeIn.addEventListener("input",function(e){ S.size=parseFloat(e.target.value); render(); });
    justify.addEventListener("change",function(e){ S.justify=e.target.checked; render(); });
    bag.addEventListener("load",place);
    window.addEventListener("resize",place);
    if(document.fonts && document.fonts.ready) document.fonts.ready.then(function(){ place(); });
    function onVariant(){ setTimeout(render,60); setTimeout(render,350); }
    document.addEventListener("change",function(e){
      var t=e.target;
      if(t && t.closest && (t.closest("variant-picker") || t.closest('[name^="options"]') || t.name==="id")) onVariant();
    });
    document.addEventListener("variant:change", onVariant);

    var uploadVersion = 0;
    function rejectFile(main, sub){
      uploadVersion++;
      S.file=null; S.fileName=""; art.removeAttribute("src");
      fileIn.value="";   // so picking the same file again still fires "change"
      dropMain.textContent=main; dropSub.textContent=sub;
      render();
    }

    // The live preview is the proof, so anything we can't draw we can't accept.
    function takeFile(f){
      if(!f) return;
      rejectFile("Choose your artwork", DROP_HINT);
      var version = uploadVersion;
      if(!OK_EXT.test(f.name)) return rejectFile("Choose your artwork", d.fileTypeError);
      if(f.size > MAX_FILE_BYTES){
        return rejectFile("Choose your artwork",
          "That file is "+(f.size/1048576).toFixed(1)+" MB. Please keep artwork under 10 MB.");
      }
      showError("");
      dropMain.textContent=f.name;
      dropSub.textContent=d.checkingArtwork;
      var isPng = /\.png$/i.test(f.name);
      var source;
      if(PDF_EXT.test(f.name)){
        source=rasterizePdf(f);
      } else {
        source=Promise.resolve().then(function(){
          if(!isPng) return;
          return f.slice(0,24).arrayBuffer().then(function(buffer){
            var bytes=new Uint8Array(buffer);
            var signature=[137,80,78,71,13,10,26,10];
            if(bytes.length<24 || !signature.every(function(v,i){return bytes[i]===v;}) ||
               String.fromCharCode.apply(null,bytes.slice(12,16))!=="IHDR") throw new Error(d.invalidPng);
          });
        }).then(function(){
          return new Promise(function(resolve,reject){
            var rd=new FileReader();
            rd.onerror=function(){reject(new Error(d.artworkReadError));};
            rd.onload=function(ev){resolve(ev.target.result);};
            rd.readAsDataURL(f);
          });
        });
      }
      source.then(function(url){
        if(version!==uploadVersion) return;
        return new Promise(function(resolve,reject){
          var preview=new Image();
          preview.onerror=function(){reject(new Error(d.artworkReadError));};
          preview.onload=function(){
            if(version!==uploadVersion) return resolve();
            if(isPng && (preview.naturalWidth<1200 && preview.naturalHeight<1200)){
              return reject(new Error(d.pngSizeError.replace('[width]',preview.naturalWidth).replace('[height]',preview.naturalHeight)));
            }
            art.onload=function(){
              if(version!==uploadVersion) return;
              S.file=f; S.fileName=f.name;
              dropSub.textContent="Click to replace";
              render();
            };
            art.onerror=function(){
              if(version===uploadVersion) rejectFile("Choose your artwork",d.artworkReadError);
            };
            art.src=url;
            resolve();
          };
          preview.src=url;
        });
      }).catch(function(error){
        if(version!==uploadVersion) return;
        rejectFile("Choose your artwork", PDF_EXT.test(f.name) ?
          "We couldn't render that file. If it's an Illustrator file, re-save it with “Create PDF Compatible File” ticked, or export a PDF or SVG." : error.message);
      });
    }

    // X on the drop zone: take the artwork back off the bag and start over.
    function clearFile(){
      showError("");
      rejectFile("Choose your artwork", DROP_HINT);
      drop.focus();
    }
    if(clearBtn){
      clearBtn.addEventListener("click",function(e){ e.preventDefault(); e.stopPropagation(); clearFile(); });
      clearBtn.addEventListener("keydown",function(e){ e.stopPropagation(); });
    }
    drop.addEventListener("click",function(){ fileIn.click(); });
    drop.addEventListener("keydown",function(e){ if(e.key==="Enter"||e.key===" "){ e.preventDefault(); fileIn.click(); }});
    fileIn.addEventListener("change",function(e){ takeFile(e.target.files[0]); });
    ["dragover","dragenter"].forEach(function(ev){ drop.addEventListener(ev,function(e){ e.preventDefault(); }); });
    drop.addEventListener("drop",function(e){ e.preventDefault(); takeFile(e.dataTransfer.files[0]); });

    (function(){
      var form=productForm(); if(!form) return;
      var a=form.querySelector("buy-buttons")||form.querySelector('[type="submit"]');
      if(a&&a.parentNode) a.parentNode.insertBefore(root,a);
    })();

    var busy=false;
    document.addEventListener("submit",function(e){
      var form=e.target;
      if(!form.action||form.action.indexOf("/cart/add")===-1) return;
      if(!ready()||busy) return;
      e.preventDefault(); e.stopImmediatePropagation(); busy=true;
      // busy alone is not enough. On success this handler never clears it and
      // relies on the redirect below, but the page stays interactive until the
      // navigation actually commits. A second click inside that window finds
      // busy true, is declined here, and falls through to a native submit of
      // the hidden inputs alone. Disabling the controls removes the window.
      var submitBtns = Array.prototype.slice.call(
        form.querySelectorAll('[type="submit"], button:not([type])'));
      submitBtns.forEach(function(b){ b.disabled = true; });
      function releaseBtns(){ submitBtns.forEach(function(b){ b.disabled = false; }); }
      showError("");
      syncProps();

      var R=(window.Shopify&&window.Shopify.routes&&window.Shopify.routes.root)||"/";
      var fd=new FormData(form);
      var qty=parseInt(fd.get("quantity")||"1",10)||1;
      var props={};
      fd.forEach(function(v,k){ var m=/^properties\[(.+)\]$/.exec(k); if(m) props[m[1]]=v; });

      function addBag(proof, stitch){
        var wantsFile = (S.mode==="logo" && S.file) || proof || stitch;
        if(wantsFile){
          var mp=new FormData();
          mp.append("id", fd.get("id")); mp.append("quantity", String(qty));
          Object.keys(props).forEach(function(k){ mp.append("properties["+k+"]", props[k]); });
          if(S.mode==="logo" && S.file){
            mp.append("properties[Artwork]", S.file, S.fileName);
            // Written here, never as hidden inputs - see syncProps().
            mp.append("properties[Artwork File]", S.fileName);
            mp.append("properties[Artwork Source]", "Customer upload");
            mp.append("properties[Digitization]", "Required");
          }
          else if(stitch) mp.append("properties[Artwork]", stitch, "front-pocket-text.svg");
          if(proof) mp.append("properties[Approved Proof]", proof, "approved-proof.png");
          return fetch(R+"cart/add.js",{ method:"POST", body:mp });
        }
        return fetch(R+"cart/add.js",{ method:"POST",
          headers:{"Content-Type":"application/json","Accept":"application/json"},
          body:JSON.stringify({ items:[{ id:fd.get("id"), quantity:qty, properties:props }] }) });
      }

      // Takes the stitch file so the line can never claim artwork the order does
      // not carry. Orders #2351 and #2354 shipped to production saying an SVG was
      // attached when generation had silently failed; that is what this prevents.
      function serviceItems(stitch){
        if(S.mode==="mono"){
          var mp={ "Text":props["Text"]||"", "Lettering":props["Lettering"]||"",
            "Thread":props["Thread"]||"",
            "Design Size":props["Design Size"]||"", "Stitch Field":props["Stitch Field"]||"",
            "Placement":"Front pocket, centered",
            "Artwork File": stitch
              ? "front-pocket-text.svg (vector, attached to the bag line)"
              : "NOT GENERATED — rebuild from Text + Lettering + Design Size before stitching" };
          if(props["Check Before Stitching"]) mp["Check Before Stitching"]=props["Check Before Stitching"];
          return [{ id:d.monoVariant, quantity:qty, properties:mp }];
        }
        var out=[{ id:d.logoVariant, quantity:qty, properties:{
          "Artwork File":S.fileName, "Design Size":props["Design Size"]||"",
          "Stitch Field":props["Stitch Field"]||"",
          "Placement":"Front pocket, centered" }}];
        // Digitizing is baked into the logo price (Snapl: $15 stitch + $20
        // digitize = $35 cost). No separate fee line.
        return out;
      }


      // Order matters for display: Shopify lists the most recently added line
      // first, so the embroidery line goes in first and the bag second — the bag
      // then sits above its embroidery in the cart and at checkout.
      var svcKeys=[];
      function rollbackSvc(){
        return svcKeys.reduce(function(chain,key){
          return chain.then(function(){
            return fetch(R+"cart/change.js",{ method:"POST",
              headers:{"Content-Type":"application/json","Accept":"application/json"},
              body:JSON.stringify({ id:key, quantity:0 }) });
          });
        }, Promise.resolve());
      }

      // Build both artefacts BEFORE anything enters the cart. The old order —
      // add the service line, then generate — meant the line was already written
      // and could not be told whether the SVG had actually been produced.
      // Nothing is added to the cart in the meantime, so a failure here still
      // leaves the cart untouched.
      var bagOk = false;
      Promise.all([
        buildProof().catch(function(){ return null; }),
        (S.mode==="mono") ? buildStitchSvg().catch(function(){ return null; }) : Promise.resolve(null)
      ])
      .then(function(both){
        var proof = both[0], stitch = both[1];

        // A missing artefact still never blocks the sale — with Lettering now on
        // the order the design can be rebuilt exactly — but it must be loud, and
        // it must be on the order rather than only in a console nobody reads.
        var warn = [];
        if(S.mode==="mono" && !stitch) warn.push("Stitch file did not generate — rebuild it from Text + Lettering + Design Size before stitching");
        if(!proof) warn.push("No approved proof was captured at checkout — confirm the design with the customer before stitching");
        if(warn.length){
          props["Check Before Stitching"] =
            (props["Check Before Stitching"] ? props["Check Before Stitching"]+" | " : "") + warn.join(" | ");
        }

        return fetch(R+"cart/add.js",{ method:"POST",
            headers:{"Content-Type":"application/json","Accept":"application/json"},
            body:JSON.stringify({ items:serviceItems(stitch) }) })
          .then(function(r){ if(!r.ok) throw new Error("service"); return r.json(); })
          .then(function(j){
            var added = j && j.items ? j.items : (j ? [j] : []);
            svcKeys = added.map(function(i){ return i.key; }).filter(Boolean);
            return addBag(proof, stitch);
          });
      })
      .then(function(r){ if(!r.ok) throw new Error("bag"); bagOk = true; return r.json(); })
      .then(function(){ window.location.href=R+"cart"; })
      .catch(function(e){
        // Roll back on ANY outcome where the embroidery line went in and the bag
        // did not. The old test was e.message === "bag", which only catches a
        // non-ok response: a dropped connection rejects with a TypeError, left
        // the embroidery line orphaned in the cart, and is how #2354 ended up
        // carrying two different thread colours.
        var done = (!bagOk && svcKeys.length) ? rollbackSvc() : Promise.resolve();
        done.catch(function(){}).then(function(){
          busy=false;
          releaseBtns();
          showError(e && e.message === "service"
            ? "We couldn't add the embroidery, so nothing was added to your cart. Please try again."
            : "We couldn't add your bag, so nothing was added to your cart. Please try again.");
        });
      });
    },true);

    render();
  }

  function boot(){
    Array.prototype.forEach.call(document.querySelectorAll("[data-fpm-root]"), init);
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
  else boot();
})();
